import type { RouteLocationNormalized } from 'vue-router'
import { APP_DEFAULT_DESCRIPTION, APP_TITLE_SUFFIX } from '@/constants/routes'
import { resolveRoutePageTitle } from '@/utils/page-title'

const ROUTE_ALTERNATE_SELECTOR = 'link[rel="alternate"][hreflang]'

function resolveAbsoluteUrl(pathOrUrl: string) {
  if (/^https?:\/\//.test(pathOrUrl)) {
    return pathOrUrl
  }

  return new URL(pathOrUrl, window.location.origin).toString()
}

function normalizeMetaContent(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function isStringRecord(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.entries(value).every(
    ([key, itemValue]) => typeof key === 'string' && typeof itemValue === 'string',
  )
}

function upsertMetaByName(name: string, content: string) {
  const head = document.head
  let element = head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('name', name)
    head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function removeMetaByName(name: string) {
  document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.remove()
}

function upsertMetaByProperty(property: string, content: string) {
  const head = document.head
  let element = head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('property', property)
    head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function upsertCanonicalLink(href: string) {
  const head = document.head
  let element = head.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', 'canonical')
    head.appendChild(element)
  }

  element.setAttribute('href', resolveAbsoluteUrl(href))
}

function applyAlternateLinks(alternatePaths?: Record<string, string>) {
  document.head.querySelectorAll<HTMLLinkElement>(ROUTE_ALTERNATE_SELECTOR).forEach((element) => {
    element.remove()
  })

  if (!alternatePaths) {
    return
  }

  Object.entries(alternatePaths).forEach(([hrefLang, href]) => {
    const element = document.createElement('link')
    element.setAttribute('rel', 'alternate')
    element.setAttribute('hreflang', hrefLang)
    element.setAttribute('href', resolveAbsoluteUrl(href))
    document.head.appendChild(element)
  })
}

function resolveOgLocale(lang: string) {
  return lang === 'en-US' ? 'en_US' : 'zh_CN'
}

function resolveCanonicalPath(route: RouteLocationNormalized) {
  const canonicalPath = normalizeMetaContent(route.meta.canonicalPath)

  if (canonicalPath) {
    return canonicalPath
  }

  if (route.name === 'UidProfileView' && typeof route.params.uid === 'string') {
    return `/${route.params.uid.toLowerCase()}`
  }

  return route.path
}

function applyRobotsMeta(robots: string) {
  if (robots) {
    upsertMetaByName('robots', robots)
    return
  }

  removeMetaByName('robots')
}

export function applyRouteHead(route: RouteLocationNormalized) {
  if (typeof document === 'undefined') {
    return
  }

  const title = resolveRoutePageTitle(route)
  const description = normalizeMetaContent(route.meta.description) || APP_DEFAULT_DESCRIPTION
  const lang = normalizeMetaContent(route.meta.lang) || 'zh-CN'
  const canonicalPath = resolveCanonicalPath(route)
  const ogLocale = normalizeMetaContent(route.meta.ogLocale) || resolveOgLocale(lang)
  const ogType = normalizeMetaContent(route.meta.ogType) || 'website'
  const robots = normalizeMetaContent(route.meta.robots)

  document.title = title
  document.documentElement.lang = lang

  upsertMetaByName('description', description)
  upsertMetaByName('twitter:title', title)
  upsertMetaByName('twitter:description', description)
  upsertMetaByProperty('og:site_name', APP_TITLE_SUFFIX)
  upsertMetaByProperty('og:title', title)
  upsertMetaByProperty('og:type', ogType)
  upsertMetaByProperty('og:description', description)
  upsertMetaByProperty('og:url', resolveAbsoluteUrl(canonicalPath))
  upsertMetaByProperty('og:locale', ogLocale)
  upsertCanonicalLink(canonicalPath)
  applyRobotsMeta(robots)
  applyAlternateLinks(
    isStringRecord(route.meta.alternatePaths) ? route.meta.alternatePaths : undefined,
  )
}
